import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import {
  FaUserPlus,
  FaMagnifyingGlass,
  FaUserGear,
  FaPhone,
  FaPenToSquare,
  FaTrashCan,
  FaFilter,
  FaEnvelope, // Tambahan ikon email
} from "react-icons/fa6";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { useHookUser } from "../../../hooks/useHookUser";
import { UserCreate } from "./UserCreate";
import { UserUpdate } from "./UserUpdate";
import { UserService } from "../../../services/UserService";
import { PopUpDeleteConfirmation } from "../../../components/popups/PopUpDeleteConfirmation";
import { BaseUrl } from "../../../utilities/BaseUrl";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-700/60" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/60 rounded-md w-28" />
          <div className="h-3 bg-slate-700/40 rounded-md w-16" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-14" />
    </div>
    <div
      className="flex justify-between items-center pt-3"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <div className="h-3 bg-slate-700/30 rounded w-24" />
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
      </div>
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
        isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isActive ? "bg-emerald-400" : "bg-red-400"
        }`}
      />
      {status || "-"}
    </span>
  );
};

/* ============================================
   KOMPONEN: UserManager
   ============================================ */
export const UserManager = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  /* ---------- Filter & Search (client-side) ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const { data, loading, refreshData } = useHookUser();

  /* ---------- Client-side Filter ---------- */
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => {
      const matchesSearch =
        item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.phone_number && item.phone_number.includes(searchTerm));
      const matchesRole =
        filterRole === "" || item?.role?.name === filterRole;
      const matchesStatus =
        filterStatus === "" || item?.status?.name === filterStatus;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [data, searchTerm, filterRole, filterStatus]);

  /* ---------- Extract unique filters ---------- */
  const roles = useMemo(
    () => [...new Set(data?.map((i) => i?.role?.name).filter(Boolean))],
    [data]
  );
  const statuses = useMemo(
    () => [...new Set(data?.map((i) => i?.status?.name).filter(Boolean))],
    [data]
  );

  /* ---------- Delete State ---------- */
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [targetDeleteId, setTargetDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const handleEdit = (item) => {
    setSelectedData(item);
    setOpenModalEdit(true);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setLoadingDelete(true);
    try {
      await UserService.delete({ id: targetDeleteId });
      refreshData();
      setOpenDeleteConfirmation(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDelete(false);
    }
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "name", label: "Nama", align: "left" },
    { key: "email", label: "Email", align: "left" },
    { key: "contact", label: "Kontak", align: "left" },
    { key: "role", label: "Role", align: "left", width: "140px" },
    { key: "status", label: "Status", align: "left", width: "130px" },
    { key: "action", label: "Aksi", align: "center", width: "100px" },
  ];

  return (
    <>
      <Helmet>
        <title>User Manager - DANELA NET</title>
        <meta
          name="description"
          content="Kelola data pengguna sistem, atur role, dan monitor status akun."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Management Pengguna
            </h2>
            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              {filteredData.length} user
            </span>
          </div>
          <BtnDefault onClick={() => setOpenModalCreate(true)}>
            <span className="hidden lg:flex items-center gap-2">
              <FaUserPlus className="text-xs" />
              Tambah Data
            </span>
            <FaUserPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label
                  htmlFor="search-user"
                  className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                >
                  Cari Data
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-user"
                    type="text"
                    placeholder="Nama, email, atau telepon..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Filter
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">
                      Semua Role
                    </option>
                    {roles.map((r) => (
                      <option key={r} value={r} className="bg-slate-800">
                        {r}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">
                      Semua Status
                    </option>
                    {statuses.map((s) => (
                      <option key={s} value={s} className="bg-slate-800">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel pengguna sistem"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {/* Loading */}
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && filteredData?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaUserGear className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Tidak ada pengguna ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {searchTerm || filterRole || filterStatus
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada data pengguna yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    filteredData?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150 cursor-pointer"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 180, 216, 0.04)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        {/* No */}
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                          {index + 1}
                        </td>

                        {/* Name & Avatar */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {!item?.image ? (
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                                  border: "1px solid rgba(0, 180, 216, 0.15)",
                                  color: "#48CAE4",
                                }}
                              >
                                {item.name?.charAt(0).toUpperCase()}
                              </div>
                            ) : (
                              <img
                                src={BaseUrl + "/storage/" + item.image}
                                alt={item.name}
                                className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
                                style={{
                                  border: "1px solid rgba(51, 65, 85, 0.3)",
                                }}
                              />
                            )}
                            <span className="font-semibold text-gray-100 truncate">
                              {item.name}
                            </span>
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-300">
                            <FaEnvelope className="text-xs text-slate-500 flex-shrink-0" />
                            <span className="text-sm truncate">
                              {item.email || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaPhone className="text-xs text-slate-500 flex-shrink-0" />
                            <span className="text-sm italic">
                              {item.phone_number || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Role */}
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium text-slate-300 bg-slate-700/30 px-2.5 py-1 rounded-lg">
                            {item?.role?.name || "-"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge status={item?.status?.name} />
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/user-manager/detail/${item.id}`)}
                              className="p-2 rounded-lg text-white bg-green-500/30 hover:bg-green-500/70 transition-all"
                              title="Detail pengguna"
                            >
                              <FaEye className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="p-2 rounded-lg text-white bg-yellow-500/30 hover:bg-yellow-500/70 transition-all"
                              title="Edit pengguna"
                            >
                              <FaPenToSquare className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setTargetDeleteId(item.id);
                                setOpenDeleteConfirmation(true);
                              }}
                              className="p-2 rounded-lg text-white bg-red-500/30 hover:bg-red-500/70 transition-all"
                              title="Hapus pengguna"
                            >
                              <FaTrashCan className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section
            className="lg:hidden space-y-3"
            aria-label="Daftar pengguna sistem"
          >
            {/* Loading */}
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </>
            )}

            {/* Empty */}
            {!loading && filteredData?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(51, 65, 85, 0.2)",
                    border: "1px solid rgba(51, 65, 85, 0.2)",
                  }}
                >
                  <FaUserGear className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">
                  Tidak ada pengguna ditemukan
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {searchTerm || filterRole || filterStatus
                    ? "Coba ubah filter pencarian"
                    : "Belum ada data pengguna"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              filteredData?.map((item, index) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(0, 180, 216, 0.2)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background =
                      "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header Card */}
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                    }}
                  >
                    <div className="flex items-center gap-2 text-slate-500 font-mono text-xs font-bold">
                      #{index + 1}
                    </div>
                    <StatusBadge status={item?.status?.name} />
                  </div>

                  {/* Konten Card */}
                  <div
                    onClick={() => navigate(`/dashboard/user-manager/detail/${item.id}`)}
                    className="p-4 cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {!item?.image ? (
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0"
                            style={{
                              background:
                                "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                              border: "1px solid rgba(0, 180, 216, 0.15)",
                              color: "#48CAE4",
                            }}
                          >
                            {item.name?.charAt(0).toUpperCase()}
                          </div>
                        ) : (
                          <img
                            src={BaseUrl + "/storage/" + item.image}
                            alt={item.name}
                            className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                            style={{
                              border: "1px solid rgba(51, 65, 85, 0.3)",
                            }}
                          />
                        )}
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">
                            {item.name}
                          </h3>
                          <span className="text-xs font-medium text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded mt-0.5 inline-block">
                            {item?.role?.name || "No Role"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Informasi Email & Telepon Mobile */}
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center gap-2 text-slate-300">
                        <FaEnvelope className="text-[11px] text-slate-500 flex-shrink-0" />
                        <span className="text-xs truncate">
                          {item.email || "-"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <FaPhone className="text-[11px] text-slate-500 flex-shrink-0" />
                        <span className="text-xs font-medium">
                          {item.phone_number || "-"}
                        </span>
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-end pt-3"
                      style={{
                        borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                      }}
                    >
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(item);
                          }}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 transition-all"
                          style={{
                            background: "rgba(0, 180, 216, 0.06)",
                            border: "1px solid rgba(0, 180, 216, 0.1)",
                          }}
                        >
                          <FaPenToSquare className="text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTargetDeleteId(item.id);
                            setOpenDeleteConfirmation(true);
                          }}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-red-400 transition-all"
                          style={{
                            background: "rgba(239, 68, 68, 0.06)",
                            border: "1px solid rgba(239, 68, 68, 0.1)",
                          }}
                        >
                          <FaTrashCan className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Modals & Popups ===== */}
      <UserCreate
        open={openModalCreate}
        setOpen={setOpenModalCreate}
        refreshData={refreshData}
      />
      <UserUpdate
        open={openModalEdit}
        setOpen={setOpenModalEdit}
        dataSelected={selectedData}
        refreshData={refreshData}
      />
      <PopUpDeleteConfirmation
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onConfirm={handleDelete}
        loading={loadingDelete}
      />
    </>
  );
};